import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  CHARACTERS,
  clampCharacterIndex,
  type CharacterDef,
} from '@game/shared';
import {
  abandonActiveSession,
  fetchActiveSession,
  meQuery,
  serverListQuery,
} from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { useGameStore } from '../game/store.js';
import { AccountPanel } from './account.jsx';
import { Button } from './button.jsx';
import { Leaderboard } from './leaderboard.jsx';

const CONTROLS: [string, string][] = [
  ['WASD', 'move'],
  ['Space', 'jump'],
  ['Shift', 'sprint'],
  ['Ctrl', 'crouch / slide'],
  ['LMB', 'fire'],
  ['RMB', 'aim'],
  ['R', 'reload'],
  ['1–4', 'weapons'],
  ['Tab', 'scoreboard'],
  ['Esc', 'pause'],
];

const NAME_KEY = 'callout:callsign';
const CHARACTER_KEY = 'callout:character';

/**
 * "Do you want to continue the match?" — shown when the server is holding a
 * seat for this identity (reload, crash) or the account is mid-match on
 * another device. Entering the game resumes the seat automatically; Abandon
 * releases it server-side.
 */
const ContinuePrompt = ({
  state,
  deadline,
  onContinue,
  onDismiss,
}: {
  state: 'reserved' | 'connected';
  deadline: number | null;
  onContinue: () => void;
  onDismiss: () => void;
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);
  const secondsLeft =
    deadline === null ? null : Math.max(0, Math.ceil((deadline - now) / 1000));

  return (
    <div className="fixed inset-0 z-20 grid place-items-center bg-black/60">
      <div className="w-[min(420px,90vw)] animate-rise rounded-xl border border-white/10 bg-surface-800 p-7 text-center backdrop-blur-xl">
        <h2 className="text-xl font-bold">
          {state === 'reserved'
            ? 'Do you want to continue the match?'
            : 'You are in a match on another device'}
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          {state === 'reserved'
            ? `Your character is frozen in the arena${secondsLeft !== null ? ` — seat held for ${secondsLeft}s` : ''}.`
            : 'Switch the match to this device? The other one will be signed out.'}
        </p>
        <div className="mt-5 flex gap-2.5">
          <Button size="lg" className="flex-1" onClick={onContinue}>
            {state === 'reserved' ? 'Continue' : 'Switch here'}
          </Button>
          <Button size="lg" variant="ghost" className="flex-1" onClick={onDismiss}>
            {state === 'reserved' ? 'Abandon' : 'Not now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const hex = (color: number): string => `#${color.toString(16).padStart(6, '0')}`;

/**
 * A miniature of the in-game blocky avatar, drawn with plain divs from the same
 * CharacterDef the renderer uses — so the picker is an honest preview.
 */
const CharacterPreview = ({ def }: { def: CharacterDef }) => (
  <div className="flex flex-col items-center">
    <div className="h-1.5 w-5 rounded-sm" style={{ background: hex(def.accent) }} />
    <div
      className="size-6 rounded-[3px]"
      style={{ background: hex(def.skin) }}
    />
    <div className="flex items-start">
      <div className="h-6 w-2 rounded-sm" style={{ background: hex(def.sleeves) }} />
      <div className="h-7 w-6" style={{ background: hex(def.torso) }} />
      <div className="h-6 w-2 rounded-sm" style={{ background: hex(def.sleeves) }} />
    </div>
    <div className="flex gap-0.5">
      <div className="h-6 w-2.5" style={{ background: hex(def.legs) }} />
      <div className="h-6 w-2.5" style={{ background: hex(def.legs) }} />
    </div>
  </div>
);

/**
 * The menu collects a callsign and a character. Everything else — which map,
 * which mode, who you fight — is decided in the walkable lobby by standing on
 * a pad.
 */
export const Menu = ({
  onPlay,
  onSettings,
}: {
  onPlay: (name: string, character: number) => void;
  onSettings: () => void;
}) => {
  const phase = useGameStore((state) => state.phase);
  const statusText = useGameStore((state) => state.statusText);
  const [name, setName] = useState('');
  const [character, setCharacter] = useState(0);
  const [showBoard, setShowBoard] = useState(false);

  useEffect(() => {
    setName(localStorage.getItem(NAME_KEY) ?? '');
    setCharacter(clampCharacterIndex(Number(localStorage.getItem(CHARACTER_KEY))));
  }, []);

  const servers = useQuery(serverListQuery);
  const me = useQuery(meQuery);
  const queryClient = useQueryClient();
  const [promptDismissed, setPromptDismissed] = useState(false);
  const active = useQuery({
    queryKey: ['session-active'],
    queryFn: fetchActiveSession,
    refetchInterval: 4000,
    retry: false,
  });
  const busy = phase === 'connecting';
  const online = servers.data?.reduce((sum, entry) => sum + entry.players, 0);
  // A signed-in account owns its callsign; guests type one.
  const accountName = me.data?.account.name ?? null;

  const pickCharacter = (index: number) => {
    setCharacter(index);
    localStorage.setItem(CHARACTER_KEY, String(index));
  };

  const play = () => {
    const callsign = accountName ?? (name.trim() || 'Operative');
    localStorage.setItem(NAME_KEY, callsign);
    onPlay(callsign, character);
  };

  const session = active.data;
  const showPrompt =
    !promptDismissed &&
    !busy &&
    (session?.state === 'reserved' || session?.state === 'connected');
  const seatDeadline =
    session?.state === 'reserved' && session.remainingMs !== undefined
      ? active.dataUpdatedAt + session.remainingMs
      : null;

  if (showBoard) {
    return (
      <div className="fixed inset-0 z-10 grid place-items-center bg-[radial-gradient(circle_at_50%_40%,#16203a,#070a12_70%)]">
        <div className="w-[min(560px,92vw)] animate-rise rounded-xl border border-white/10 bg-surface-800/90 p-9 backdrop-blur-xl">
          <h1 className="text-2xl font-extrabold tracking-tight">Leaderboard</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Career standings — signed-in players only
          </p>
          <div className="mt-5 max-h-[50vh] overflow-y-auto">
            <Leaderboard />
          </div>
          <Button
            size="lg"
            variant="ghost"
            className="mt-5"
            onClick={() => setShowBoard(false)}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    // flex + my-auto instead of grid centering: when the card is taller than a
    // landscape phone's viewport the auto margins collapse to zero and the
    // container scrolls, instead of clipping both ends unreachably.
    <div className="fixed inset-0 z-10 flex justify-center overflow-y-auto overscroll-contain bg-[radial-gradient(circle_at_50%_40%,#16203a,#070a12_70%)]">
      <div className="my-auto w-[min(560px,92vw)] shrink-0 animate-rise rounded-xl border border-white/10 bg-surface-800/90 p-9 backdrop-blur-xl [@media(max-height:600px)]:my-3 [@media(max-height:600px)]:p-5">
        <h1 className="text-4xl font-extrabold tracking-tight">
          CALL<span className="font-light text-team-blue">OUT</span>
        </h1>
        <p className="mt-1.5 text-sm text-ink-muted">
          Greybox arena · movement shooter
        </p>

        <AccountPanel />

        <label className="mt-5 block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            Callsign
          </span>
          <input
            value={accountName ?? name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && play()}
            maxLength={20}
            placeholder="Operative"
            autoComplete="off"
            disabled={accountName !== null}
            title={accountName !== null ? 'Your account owns this callsign' : undefined}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3.5 py-2.5 text-ink outline-none focus:border-team-blue disabled:opacity-60"
          />
        </label>

        <div className="mt-5">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            Character
          </span>
          <div className="grid grid-cols-6 gap-2">
            {CHARACTERS.map((def, index) => (
              <button
                key={def.id}
                onClick={() => pickCharacter(index)}
                title={def.tagline}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-lg border py-2.5 transition-colors',
                  index === character
                    ? 'border-team-blue bg-team-blue/15'
                    : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/10',
                )}
              >
                <CharacterPreview def={def} />
                <span
                  className={cn(
                    'text-[11px] font-semibold',
                    index === character ? 'text-team-blue' : 'text-ink-muted',
                  )}
                >
                  {def.name}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 min-h-[1em] text-xs text-ink-muted">
            {CHARACTERS[character]?.tagline}
          </p>
        </div>

        <Button size="lg" className="mt-4" onClick={play} disabled={busy}>
          {busy ? statusText || 'Connecting…' : 'Enter Lobby'}
        </Button>
        <div className="mt-2.5 flex gap-2.5">
          <Button size="lg" variant="ghost" className="flex-1" onClick={() => setShowBoard(true)}>
            Leaderboard
          </Button>
          <Button size="lg" variant="ghost" className="flex-1" onClick={onSettings}>
            Settings
          </Button>
        </div>

        <p
          className={cn(
            'mt-3.5 min-h-[1.2em] text-sm',
            phase === 'disconnected' ? 'text-team-red' : 'text-ink-muted',
          )}
        >
          {phase === 'disconnected'
            ? statusText
            : servers.isError
              ? 'Game server unreachable — start it with npm run dev:server'
              : online !== undefined
                ? `${online} player${online === 1 ? '' : 's'} online · walk onto a pad to start a match`
                : 'Looking for the lobby…'}
        </p>

        <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-5 text-[13px] text-ink-muted">
          {CONTROLS.map(([key, action]) => (
            <li key={key}>
              <kbd className="mr-1.5 inline-block min-w-5 rounded border border-white/15 bg-white/10 px-1.5 py-px text-center text-[11px]">
                {key}
              </kbd>
              {action}
            </li>
          ))}
        </ul>
      </div>

      {showPrompt && session && (
        <ContinuePrompt
          state={session.state as 'reserved' | 'connected'}
          deadline={seatDeadline}
          onContinue={play}
          onDismiss={() => {
            setPromptDismissed(true);
            if (session.state === 'reserved') {
              void abandonActiveSession().then(() =>
                queryClient.invalidateQueries({ queryKey: ['session-active'] }),
              );
            }
          }}
        />
      )}
    </div>
  );
};
